import * as React from 'react'
import { FocusEvent, FormEvent, KeyboardEvent } from 'react'
import { ITextField, ITextFieldProps, TextField } from '@fluentui/react'

export interface IFormattableTextFieldProps extends ITextFieldProps {
    // Filter the value during editing.
    filter?: (value: string, ...args) => string
    // Format the value when it is being submitted.
    format?: (value: string, ...args) => string
    // Event that is dispatched when formatting should occur.
    onFormat?: (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => void
}

export interface IFormattableTextFieldState {
    value: string
    filter: (value: string, ...args) => string
    format: (value: string, ...args) => string
}

export default class FormattableTextField<P extends IFormattableTextFieldProps = IFormattableTextFieldProps> extends React.Component<
    P,
    IFormattableTextFieldState
> {
    public state: IFormattableTextFieldState = {
        value: this.props.value,
        filter: this.props.filter || this.filter,
        format: this.props.format || this.format,
    }
    public static defaultProps: Partial<IFormattableTextFieldProps>
    protected ref = React.createRef<ITextField>()

    public static getDerivedStateFromProps(
        nextProps: Readonly<IFormattableTextFieldProps>,
        prevState: Readonly<IFormattableTextFieldState>,
    ): IFormattableTextFieldState {
        return {
            ...prevState,
            value: prevState.format((nextProps.value || '').toString()),
        }
    }

    public render(): React.ReactNode {
        return (
            <TextField
                componentRef={this.ref}
                onClick={(event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => event.target.select()}
                onFocus={(event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => event.target.select()}
                onLoad={this.onFormat.bind(this)}
                onBlur={this.onFormat.bind(this)}
                onSubmit={this.onFormat.bind(this)}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => event.key === 'Enter' && this.onFormat(event)}
                {...FormattableTextField.defaultProps}
                {...this.props}
                value={this.state.value}
                onChange={this.onChange.bind(this)}
            />
        )
    }

    protected onChange(event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void {
        // Propagate to any parent event listeners.
        if (this.props.onChange) {
            ;(event.target as HTMLInputElement | HTMLTextAreaElement).value = this.state.format(value)
            this.props.onChange(event, (event.target as HTMLInputElement | HTMLTextAreaElement).value)
        }
    }

    protected onFormat(event: FormEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        const value = this.state.filter.call(this, event.currentTarget.value)

        // Do some regular formatting to ensure that we have a valid value.
        this.onChange(event, value)

        // Propagate to any event listeners on this Component.
        if (this.props.onFormat) {
            this.props.onFormat(event, value)
        }
    }

    protected filter(value: string = ''): string {
        return value
    }

    protected format(value: string = ''): string {
        return value
    }
}
